// Real API adapter — maps the existing FastAPI /api/* surface (jobs,
// packs, explorer, ai) into the prototype-flavored Api shape that
// pages/components use.
//
// Design notes:
//   * The real backend requires an authenticated org connection for
//     /api/jobs/* and /api/explorer/*. The prototype intentionally has
//     no auth UI, so when the backend returns 401 we fall back to the
//     mock data for that surface. This keeps the new UX usable in dev
//     orgs and demos without forcing a login round-trip.
//   * Streaming progress (subscribeToRun) is implemented with simple
//     2-second polling against GET /api/jobs/{id}, which is cheap and
//     avoids a WebSocket dependency.
//   * Templates and Playbooks have no clean public endpoint, so we
//     hold the mock seed data for those surfaces (the prototype itself
//     treats them as curated UX, not user-generated content).

import {
  PHASES,
  PLAYBOOKS,
  PROFILES_LIST,
  RECENT_RUNS,
  STORIES,
  TEMPLATES,
} from "@/lib/data";
import { apiCall, apiSoft } from "@/lib/api/http";
import type {
  JobPace,
  OrgInventory,
  Profile,
  Run,
  RunConfig,
  RunPhase,
  RunStatus,
  Story,
  TableauPatOneOff,
  TableauProfile,
  Template,
} from "@/lib/types";

interface RawTableauProfile {
  id: string;
  label: string;
  server_url: string;
  site_name: string;
  pat_name: string;
  is_default: boolean;
  created_at: string;
  last_used_at: string | null;
  last_validated_at: string | null;
}

function mapTableauProfile(p: RawTableauProfile): TableauProfile {
  return {
    id: p.id,
    label: p.label,
    serverUrl: p.server_url,
    siteName: p.site_name,
    patName: p.pat_name,
    isDefault: p.is_default,
    createdAt: p.created_at,
    lastUsedAt: p.last_used_at,
    lastValidatedAt: p.last_validated_at,
  };
}

async function call<T>(
  path: string,
  opts: { method?: "GET" | "POST" | "PUT" | "DELETE"; body?: unknown } = {},
): Promise<T | null> {
  return apiSoft<T>(`/api${path}`, opts);
}

interface RawPack {
  id: string;
  name: string;
  industry?: string;
  description?: string;
  product_count?: number;
  use_count?: number;
  status?: string;
  source?: string;
  logo_url?: string | null;
  icon_key?: string | null;
  review_notes?: string | null;
  created_by?: string | null;
  can_revert?: boolean;
  version?: number;
  rating?: number;
  rating_count?: number;
  // PR 2 (2026-06-03): packs that declare an Insights App
  // (``"insights_app": "sales_insights" | "service_insights"`` in
  // the JSON) stay on the ATF deploy path. The wizard hides the
  // Phase 8 Tab Next tile for them.
  insights_app?: string | null;
}

// Statuses we consider visible in the Stage 1 picker. We deliberately
// surface the SE's own submitted/rejected packs alongside their
// drafts: the backend `/packs` endpoint already scopes the non-public
// statuses (draft/submitted/rejected) to the author, so there's no
// leak risk, and the SE wants to see "everything I'm working on" in
// one place — the My Drafts drawer pills the lifecycle status so they
// can tell what state each one is in at a glance.
const PICKER_STATUSES = new Set<string>([
  "built_in",
  "approved",
  "featured",
  "draft",
  "submitted",
  "rejected",
]);

function isPickerEligible(p: Profile): boolean {
  if (!p.status) return p.type === "official";
  return PICKER_STATUSES.has(p.status);
}

function mapPackToProfile(p: RawPack): Profile {
  const status = (p.status ?? "draft") as Profile["status"];
  // Origin (built-in vs user-created), NOT lifecycle, drives the
  // "official vs custom" bucket. Approved/featured user packs still
  // belong with the user's drafts in the "custom" sense — they're
  // submissions to the community library, not Generator-maintained.
  const isOfficial = p.source === "built_in" || status === "built_in";
  return {
    id: p.id,
    type: isOfficial ? "official" : "custom",
    name: p.name,
    industry: p.industry ?? "Custom",
    products: p.product_count ?? 0,
    stories: 0,
    // Always go through the backend logo endpoint so we get the SVG
    // monogram fallback for free if the pack has no logo_url. Bypass
    // when the pack already shipped a logo URL we trust.
    logo: p.logo_url || `/api/packs/${encodeURIComponent(p.id)}/logo`,
    description: p.description ?? "",
    useCount: p.use_count ?? 0,
    featured: status === "featured",
    custom: !isOfficial,
    draft: status === "draft",
    status,
    source: p.source,
    reviewNotes: p.review_notes ?? null,
    createdBy: p.created_by ?? null,
    canRevert: p.can_revert ?? false,
    version: p.version,
    ratingAvg: p.rating ?? 0,
    ratingCount: p.rating_count ?? 0,
    insightsApp:
      p.insights_app === "sales_insights" || p.insights_app === "service_insights"
        ? p.insights_app
        : null,
  };
}

interface RawJob {
  id: string;
  job_id?: string;
  job_type?: string;
  status: string;
  progress_pct?: number;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
  config?: Record<string, unknown>;
  error_message?: string | null;
  current_step?: string | null;
  steps_log?: { step: string; status?: string; pct?: number; log?: string }[];
  total_steps?: number;
  is_stale?: boolean;
  stale_reason?: string | null;
  elapsed?: number | null;
}

export type RawJobShape = RawJob;

// Lift partial-success metadata out of the job's stored config blob.
// The backend keeps a "completed" job row even when the analytics
// layer silently failed (see ``pipeline_runner.collect_analytics_failures``)
// and lights up ``cfg.partial_success`` instead. This helper centralises
// the unwrap so every consumer (the API mapper, future tests, the
// monitor banner) sees the same canonical shape.
//
// PROD-A3 (2026-04-21).
function readPartialSuccess(cfg: Record<string, unknown>): {
  partialSuccess: boolean;
  analyticsFailureSummary: string[];
  failedSobjects: string[];
} {
  const partialSuccess = cfg.partial_success === true;
  const rawSummary = cfg.analytics_failure_summary;
  const phase6 = (cfg.phase6_outputs as Record<string, unknown> | undefined) ?? {};
  const phase6Summary = phase6?.["analytics_failure_summary"];
  const summary = Array.isArray(rawSummary)
    ? (rawSummary as unknown[])
    : Array.isArray(phase6Summary)
      ? (phase6Summary as unknown[])
      : [];
  const failed = Array.isArray(cfg.failed_sobjects)
    ? (cfg.failed_sobjects as unknown[])
    : [];
  return {
    partialSuccess,
    analyticsFailureSummary: summary
      .map((s) => (typeof s === "string" ? s : ""))
      .filter((s) => s.length > 0),
    failedSobjects: failed
      .map((s) => (typeof s === "string" ? s : ""))
      .filter((s) => s.length > 0),
  };
}

function mapStatus(s: string, partialSuccess: boolean): RunStatus {
  // PROD-A3 (2026-04-21): a "completed" job whose config carries
  // ``partial_success=true`` is rendered as ``complete_with_errors``
  // so every downstream UI surface (Done page, runs badge, monitor
  // chip, notification bell) can branch on a single first-class
  // status instead of every consumer having to re-derive the bit
  // from ``run.config?.partial_success``.
  if (s === "completed") return partialSuccess ? "complete_with_errors" : "complete";
  if (s === "running") return "running";
  if (s === "queued") return "queued";
  if (s === "failed") return "failed";
  if (s === "cancelled") return "cancelled";
  return "queued";
}

function mapJobToRun(j: RawJob): Run {
  const cfg = j.config ?? {};
  const packId = (cfg.industry_pack_id as string) ?? "";
  const packName = (cfg.pack_name as string) ?? packId;
  const volume = ((cfg.volume_preset as string) ?? "medium").toLowerCase();
  const isHyper = (cfg.output_mode as string) === "hyper";
  // Hyper-mode runs skip the CRM provisioning entirely and emit a .tdsx
  // file. Surface that as the `tdsx` layer so the UI knows to offer the
  // download instead of "Open in Salesforce".
  const layers: Run["layers"] = isHyper ? ["tdsx"] : ["crm"];
  if (!isHyper && cfg.include_data_cloud) layers.push("dc", "sdm");
  if (!isHyper && cfg.include_tableau_next) layers.push("apps", "tabnext");
  const { partialSuccess, analyticsFailureSummary, failedSobjects } =
    readPartialSuccess(cfg);
  const recordsProcessed = typeof cfg.records_processed === "number"
    ? (cfg.records_processed as number)
    : undefined;
  const recordsFailed = typeof cfg.records_failed === "number"
    ? (cfg.records_failed as number)
    : undefined;
  return {
    id: j.id,
    profileId: packId,
    profile: packName,
    volume: volume.charAt(0).toUpperCase() + volume.slice(1),
    org: isHyper ? "TDSX file" : "connected org",
    status: mapStatus(j.status, partialSuccess),
    startedAt: j.started_at ?? j.created_at ?? undefined,
    completed: j.completed_at ?? undefined,
    duration: j.completed_at && j.started_at
      ? `${Math.round((Date.parse(j.completed_at) - Date.parse(j.started_at)) / 60000)} min`
      : undefined,
    storiesCount: ((cfg.selected_stories as string[]) ?? []).length,
    stories: (cfg.selected_stories as string[]) ?? [],
    owner: "you",
    layers,
    cloud: isHyper
      ? "Tableau"
      : cfg.include_sales_cloud && cfg.include_service_cloud
        ? "Sales + Service"
        : cfg.include_sales_cloud
          ? "Sales only"
          : cfg.include_service_cloud
            ? "Service only"
            : "—",
    errorMsg: j.error_message ?? undefined,
    phases: synthPhases(j),
    kind: j.job_type,
    partialSuccess: partialSuccess || undefined,
    analyticsFailureSummary: analyticsFailureSummary.length > 0
      ? analyticsFailureSummary
      : undefined,
    failedSobjects: failedSobjects.length > 0 ? failedSobjects : undefined,
    recordsProcessed,
    recordsFailed,
  };
}

// PROD-A3 (2026-04-21): exported for ``mapJobToRun.test.ts`` so the
// partial-success status derivation has a unit-testable surface. Kept
// behind an underscore prefix to signal "test-only — don't import in
// product code".
export { mapJobToRun as __mapJobToRunForTesting };

function synthPhases(j: RawJob): RunPhase[] {
  // Backend exposes step-level progress via current_step + steps_log.
  // Group those into the prototype's high-level phases for the StageStrip.
  const overall = j.progress_pct ?? 0;
  const liveStep = (j.current_step ?? "").trim();
  // Substitute generic step labels ("Accounts") with config-derived
  // targets ("Accounts · target 50") so the fallback log doesn't lie
  // when nothing has streamed yet. Counts come from the run config the
  // worker recorded — never from a static guess.
  const cfg = (j.config ?? {}) as Record<string, unknown>;
  const tAccounts = (cfg.accounts_target as number | undefined)
    ?? (cfg.target_accounts as number | undefined);
  const tOpps = (cfg.opportunities_target as number | undefined)
    ?? (cfg.target_opportunities as number | undefined);
  const tCases = (cfg.cases_target as number | undefined)
    ?? (cfg.target_cases as number | undefined);
  const tContacts = (cfg.contacts_target as number | undefined)
    ?? (cfg.target_contacts as number | undefined);
  const tLeads = (cfg.leads_target as number | undefined)
    ?? (cfg.target_leads as number | undefined);
  const labelWithTarget = (raw: string): string => {
    if (raw === "Accounts" && tAccounts) return `Accounts · target ${tAccounts.toLocaleString()}`;
    if (raw === "Contacts" && tContacts) return `Contacts · target ${tContacts.toLocaleString()}`;
    if (raw === "Cases" && tCases) return `Cases · target ${tCases.toLocaleString()}`;
    if (raw === "Opportunities" && tOpps) return `Opportunities · target ${tOpps.toLocaleString()}`;
    if (raw === "Leads" && tLeads) return `Leads · target ${tLeads.toLocaleString()}`;
    return raw;
  };
  return PHASES.map((p, idx) => {
    const phasePct = Math.max(0, Math.min(100, overall * PHASES.length - idx * 100));
    const status: RunPhase["status"] =
      j.status === "failed" && phasePct > 0 && phasePct < 100
        ? "failed"
        : phasePct >= 100
          ? "done"
          : phasePct > 0
            ? "running"
            : "queued";
    const baseSteps = p.steps.map((label) => ({
      label: labelWithTarget(label),
      done: phasePct >= 100,
    }));
    // For the running phase, surface the backend's actual current_step
    // text so the terminal log shows the real worker activity (e.g.
    // "generate_opportunities · 2,400 / 5,000 records"). Falls back to
    // the canned step list when the backend hasn't reported yet.
    if (status === "running" && liveStep) {
      return {
        id: p.id,
        label: p.label,
        status,
        pct: Math.round(phasePct),
        steps: [{ label: liveStep, done: false }, ...baseSteps],
      };
    }
    return {
      id: p.id,
      label: p.label,
      status,
      pct: Math.round(phasePct),
      steps: baseSteps,
    };
  });
}

export const realApi = {
  async listProfiles(): Promise<Profile[]> {
    const raw = await call<RawPack[]>("/packs");
    if (!raw) return PROFILES_LIST;
    return raw.map(mapPackToProfile);
  },
  async getProfile(id: string): Promise<Profile | undefined> {
    const raw = await call<
      RawPack & {
        company?: { name?: string; industry?: string; description?: string };
        products?: unknown[];
        stories?: { descriptions?: Record<string, unknown> } | unknown;
      }
    >(`/packs/${id}`);
    if (!raw) return PROFILES_LIST.find((p) => p.id === id);
    const stories = raw.stories as { descriptions?: Record<string, unknown> } | undefined;
    const storyCount = stories?.descriptions
      ? Object.keys(stories.descriptions).length
      : 0;
    const productCount = Array.isArray(raw.products) ? raw.products.length : raw.product_count ?? 0;
    return {
      ...mapPackToProfile({
        id,
        name: raw.company?.name ?? raw.name ?? id,
        industry: raw.company?.industry ?? raw.industry,
        description: raw.company?.description ?? raw.description,
        product_count: productCount,
        status: raw.status,
        source: raw.source,
        logo_url: raw.logo_url,
      }),
      stories: storyCount,
    };
  },
  async listPacks(): Promise<Profile[]> {
    // The Build → Stage 1 picker needs to surface every pack the SE
    // could legitimately use as the basis for a run:
    //   * built-in (Generator-maintained, always visible)
    //   * approved + featured community packs (curated, visible to all)
    //   * the SE's own drafts/submissions/rejections (visible to author)
    //
    // Previously we returned only `type === "official"`, which meant
    // an SE who had just AI-generated and approved their own pack on
    // the Profiles page would land on Build, see only the 3 built-in
    // tiles, and the flight-plan header would render "New custom
    // profile / Unnamed" because packs.find(p.id === selectedId)
    // resolved to undefined.
    //
    // The backend `/packs` endpoint already scopes to packs visible to
    // the caller (built-in + approved/featured + own); we just need to
    // stop hiding the non-built-in ones from the picker.
    const raw = await call<RawPack[]>("/packs");
    if (!raw) return PROFILES_LIST.filter((p) => p.type === "official");
    return raw.map(mapPackToProfile).filter((p) => isPickerEligible(p));
  },
  async listStories(): Promise<Story[]> {
    // The pack file's stories.descriptions is the source of truth, but we
    // surface a unified list across packs in the UI. Fall back to seed.
    return STORIES;
  },
  async listTemplates(): Promise<Template[]> {
    return TEMPLATES;
  },
  async listPlaybooks(): Promise<typeof PLAYBOOKS> {
    return PLAYBOOKS;
  },
  async listRuns(): Promise<Run[]> {
    const raw = await call<RawJob[]>("/jobs?limit=50");
    if (!raw) return RECENT_RUNS;
    return raw.map(mapJobToRun);
  },
  async getRun(id: string): Promise<Run | undefined> {
    const raw = await call<RawJob>(`/jobs/${id}`);
    if (!raw) return RECENT_RUNS.find((r) => r.id === id);
    return mapJobToRun({ ...raw, id: raw.job_id ?? raw.id });
  },
  // Single-fetch variant used by the Monitor page so we hit GET /api/jobs/:id
  // *once* per poll cycle instead of three times (run + run-raw + pace was
  // blowing the 60/min rate limit and starving the EtaChip query of fresh
  // pace data on long-running phases).
  async getRunWithRaw(
    id: string,
  ): Promise<{ run: Run | undefined; raw: RawJob | null }> {
    const raw = await call<RawJob>(`/jobs/${id}`);
    if (!raw) {
      return {
        run: RECENT_RUNS.find((r) => r.id === id),
        raw: null,
      };
    }
    const normalized = { ...raw, id: raw.job_id ?? raw.id };
    return { run: mapJobToRun(normalized), raw: normalized };
  },
  async createRun(config: RunConfig): Promise<Run> {
    // TDSX mode = generate a Tableau Packaged Datasource (no CRM provisioning).
    // Backend toggles this on with output_mode="hyper". The worker still
    // needs include_sales_cloud / include_service_cloud to be TRUE so the
    // generators that produce Opportunities, Cases, Leads, etc. actually
    // run — pipeline_runner skips the CRM-only DEPLOY steps (forecast
    // quotas, omni channel, etc.) on its own when output_mode == "hyper".
    // Forcing the cloud flags to false here was the bug that made TDSX
    // files come out empty.
    const isTdsxOnly =
      config.mode === "tdsx_only" ||
      (config.pipeline.tdsx && !config.pipeline.crm);
    const isHyper = isTdsxOnly || config.pipeline.tdsx;
    // For TDSX-only flows the user typically doesn't visit the cloud step,
    // so default both clouds to ON when isTdsxOnly is true and the user
    // didn't explicitly opt one out.
    //
    // CRITICAL: gate on `config.pipeline.crm` (the Stage-4 layer toggle).
    // Without this, unchecking the CRM layer in Stage 4 still sent
    // `include_sales_cloud=true / include_service_cloud=true` to the
    // backend (because Stage 3 still had Sales+Service ticked), and the
    // backend's `dc_only_mode` short-circuit at pipeline_runner.py:2814
    // never fired — every "DC-only re-run" silently re-ran the full 50
    // minute pipeline. See KNOWN_ISSUES CRITICAL-2.
    const crmRequested = isTdsxOnly || config.pipeline.crm;
    const includeSales =
      crmRequested && (isTdsxOnly ? config.salesCloud !== false : config.salesCloud);
    const includeService =
      crmRequested && (isTdsxOnly ? config.serviceCloud !== false : config.serviceCloud);
    // Phase 6 scope is derived from the Stage-4 layer toggles. V2 deliberately
    // avoids an explicit 6-checkbox surface for the granular deploy flags
    // (see CURSOR_SPEC_TC26_PHASE6_SCOPE.md Q2) — the coarse layer toggles
    // map onto the per-template flags here, and we let the backend's
    // phase6_gates helper apply the policy. When the pre-release Service
    // Insights package ships post-TC26, add an explicit pipeline.service
    // sub-toggle and wire it into deploy_service_insights below.
    const isDcOrTn = !isHyper && (config.pipeline.dc || config.pipeline.tabnext);
    // Auto-refresh whenever DC provisioning is requested. Without this,
    // freshly-deployed CRM kits & field maps leave DMOs empty and every
    // downstream consumer (SDM authoring, sample dashboards, Sales
    // Insights app) silently produces nothing. Mirrors the legacy
    // Configure.jsx auto-enable on `deployDataKits` (frontend/Configure
    // useEffect ~line 141). `pipeline.refresh` is the standalone
    // "Refresh / extend" preset toggle — keep it as an explicit override.
    // The `pipeline.apps` preset enables the Tableau Next app stack.
    //
    // 2026-05-12 (GA): the C360 SDM tile and the Insights Apps tile
    // are both live for production use. Tile-to-flag mapping:
    //
    //   * ``config.pipeline.sdm`` → ``deploy_c360_sdm`` (the stock
    //     ``sfdc_internal__C360UnifiedSemanticModel``). Verified
    //     SuccessStatus on training orgs in
    //     ``OVERNIGHT_STATUS_2026-04-30.md``. Even if the template
    //     itself trips the platform's "Field Ids should not be empty"
    //     validator on an edge-case org, ``ATFDeployer`` short-
    //     circuits the cascade and surfaces a clear "Reprovision the
    //     org" error rather than damaging downstream apps (see the
    //     cascade fail-fast + damaged-org preflight in
    //     ``app/services/atf_deployer.py``).
    //   * ``config.pipeline.apps`` → ``deploy_sales_insights_app`` +
    //     ``deploy_sales_insights_embedded_app`` +
    //     ``deploy_sample_dashboards`` (top-level body field below).
    //
    // 2026-06-05 (Service Insights v4.1.2 swap):
    //   The prior trio of SI variants (legacy / redesign / with-EDC)
    //   was retired in favor of a single canonical v4.1.2 bundle
    //   (custom ``ServiceInsights`` AppFrameworkTemplateBundle, no
    //   ``sfdc_internal__`` prefix). The Insights Apps tile now wires
    //   ``pipeline.apps`` to BOTH Sales Insights (live) and Service
    //   Insights v4.1.2 (newly introduced). See
    //   docs/SERVICE_INSIGHTS_V412_SWAP.md (TBD) for the rationale
    //   and a per-test summary of what regression coverage moved.
    //
    // The following stay hardcoded ``false`` because they aren't yet
    // safe to enable on fresh demo orgs:
    //
    //   * ``deploy_sales_insights_sdm`` / ``deploy_c360_metrics`` —
    //     out of canonical-stack scope; not required for the live
    //     Insights Apps suite.
    //   * ``deploy_service_insights_sdm`` — the v4.1.2 bundle ships
    //     its own SDMs in ``sdms/``, so the standalone SI SDM
    //     template is rarely useful and stays opt-in via direct
    //     phase5_steps overrides.
    //
    // The Embedded SI variant stays live: the bundle was patched
    // (``scripts/patch_atf_embed_sales_user_role_flattened.py``) to
    // drop the ``User_Role_Flattened__dlm`` reference and has been
    // observed reaching ``SuccessStatus`` on Cirrus pack runs.
    //
    // ``refresh_data_streams`` stays auto-true with ``dc`` so the
    // backend gate fires; the ``SKIP_BROWSER_SETUP`` short-circuit
    // in pipeline_runner._run_phase6_async handles the actual no-op
    // so the UI doesn't have to know about Playwright.
    // PR 2 (2026-06-03): ``deploy_workspaces`` retired per the locked
    // hard-cut decision. Tab Next workspace authoring now runs in
    // Phase 8 via ``body.tableau_next`` (set further below). The
    // ``tabnext`` pipeline-toggle now drives Phase 8, NOT
    // ``phase5_steps.deploy_workspaces``.
    const phase5Steps = isDcOrTn
      ? {
          deploy_c360_sdm: !!config.pipeline.sdm,
          deploy_sales_insights_app: !!config.pipeline.apps,
          deploy_sales_insights_embedded_app: !!config.pipeline.apps,
          deploy_service_insights: !!config.pipeline.apps,
          deploy_c360_metrics: false,
          deploy_sales_insights_sdm: false,
          deploy_service_insights_sdm: false,
          refresh_data_streams:
            !!config.pipeline.refresh || !!config.pipeline.dc,
        }
      : undefined;
    // deploy_sample_dashboards is the TC26 Sample Sales Cloud package
    // (TabNext Package & Deploy API). It lives at the TOP LEVEL of the
    // POST body — NOT inside phase5_steps — because the backend gate
    // (see phase6_gates.should_deploy_sample_dashboards) reads it
    // directly from cfg. TC26 scope ties it to the Apps layer toggle.
    const body: Record<string, unknown> = {
      industry_pack_id: config.profileId,
      volume_preset: config.volume,
      demo_mode: true,
      include_sales_cloud: includeSales,
      include_service_cloud: includeService,
      include_sla: true,
      // Backend accepts "additive" | "smart" | "clean". The dedicated
      // `clean_before_run` flag is the legacy alias for merge_mode="clean".
      merge_mode: config.mergeMode === "clean" ? "clean" : config.mergeMode,
      clean_before_run: config.mergeMode === "clean",
      selected_stories: config.stories,
      // Data Cloud / Tableau Next layers don't apply to a hyper file build.
      include_data_cloud: isHyper ? false : config.pipeline.dc,
      include_tableau_next: isHyper ? false : config.pipeline.tabnext,
      output_mode: isHyper ? "hyper" : "crm",
      nl_instructions: config.aiCustomPrompt ?? "",
      // ``config.pipeline.apps`` activates the full Insights Apps
      // stack: the Sales/Service Insights ATF templates above + this
      // TC26 Sample Sales Cloud Dashboard package (TabNext Package &
      // Deploy API). Same tile drives both because they ship together
      // in the "Apps" layer of the demo.
      deploy_sample_dashboards: isDcOrTn && !!config.pipeline.apps,
    };
    if (phase5Steps) {
      body.phase5_steps = phase5Steps;
    }
    // PR 1 Week 3 (2026-06-01): when the TDSX layer is on, fold
    // the Stage 5 Tableau Outputs panel into the request so
    // ``pipeline_runner._run_phase7`` can decide which sub-steps
    // (TWBX, Publish, Pulse) to run. Always sent — the backend
    // gate (`_phase7_requested`) treats an all-false / absent
    // block as "TDSX-only legacy behaviour" which matches the
    // pre-PR1 contract.
    if (isHyper && config.tableauOutputs) {
      const t = config.tableauOutputs;
      // Backend keys match pipeline_runner._phase7_requested:
      //   build_tdsx / build_twbx / publish_to_cloud / create_pulse_metrics
      const tableauOutputs: Record<string, unknown> = {
        build_tdsx: !!t.tdsx,
        build_twbx: !!t.twbx,
        publish_to_cloud: !!t.publish,
        create_pulse_metrics: !!t.pulse,
      };
      if (t.patProfileId) {
        tableauOutputs.pat_profile_id = t.patProfileId;
      }
      if (t.patOneOff) {
        tableauOutputs.pat_one_off = {
          server_url: t.patOneOff.serverUrl,
          site_name: t.patOneOff.siteName,
          pat_name: t.patOneOff.patName,
          pat_secret: t.patOneOff.patSecret,
        };
      }
      body.tableau_outputs = tableauOutputs;
    }
    // PR 2 (2026-06-03): direct Tab Next authoring (Phase 8) — runs
    // independently of Phase 7 / TDSX. Triggered by the ``tabnext``
    // pipeline-toggle for non-SI packs. SI / Service-Insights packs
    // continue through ATF (Phase 6); the wizard hides the Tab Next
    // tile for them and the route returns 422 if the body slips
    // through anyway. The Phase 8 sub-step toggles all default to
    // "true" so a user who picks the tile gets the full ladder
    // (workspace + boards + metrics) without extra clicks.
    if (!isHyper && config.pipeline.tabnext) {
      body.tableau_next = {
        build_workspace: true,
        build_boards:    true,
        build_metrics:   true,
      };
    }
    // Use apiCall (not the soft variant) so 4xx/5xx surface as a
    // throwable error. The legacy "synthesise a fake run id and pretend
    // we queued" fallback masked real backend rejections (422 schema
    // failures, 409 active-job conflicts, 429 rate caps) — the user
    // landed on a Monitor URL that polled a non-existent UUID forever.
    // Now the React Query mutation's onError fires and the caller can
    // toast a real reason ("Connect a Salesforce org first.",
    // "Already have a job in progress.", etc).
    const raw = await apiCall<{ job_id: string; status: string }>(
      "/api/jobs/provision",
      { method: "POST", body },
    );
    const detail = await this.getRun(raw.job_id);
    // Fallback Run shape used only when ``getRun`` can't fetch the
    // freshly-created job (e.g. eventual-consistency lag). No config
    // body is available here so partial-success defaults to false —
    // the next poll will hydrate the real status.
    return detail ?? {
      id: raw.job_id,
      profileId: config.profileId,
      volume: config.volume,
      org: "—",
      status: mapStatus(raw.status, false),
      storiesCount: config.stories.length,
      owner: "you",
      layers: [],
      cloud: "—",
    };
  },
  subscribeToRun(
    runId: string,
    onPhase: (phase: RunPhase) => void,
    onComplete: () => void,
  ): () => void {
    // Polling-only. Two reasons WS was disabled:
    //   1. Heroku's edge proxy drops idle sockets after ~55s; the v1
    //      handler used to navigate to /done on close, killing the
    //      user's Monitor session.
    //   2. WS frames and the DB poll can disagree on `pct` for a brief
    //      moment, which made the bar oscillate ("flashing back to 0").
    // 2s polling against the same `progress_svc` snapshot the WS would
    // have read is plenty fast for this UI and produces a single
    // monotonic timeline.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastStatus = "queued";

    const isTerminal = (s: string): boolean =>
      s === "complete" || s === "completed" || s === "failed" || s === "cancelled";

    const finish = (): void => {
      if (cancelled) return;
      cancelled = true;
      if (timer) clearTimeout(timer);
      onComplete();
    };

    const tickPoll = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const run = await this.getRun(runId);
        if (cancelled) return;
        if (run) {
          if (run.status) lastStatus = run.status;
          (run.phases ?? []).forEach((p) => onPhase(p));
          if (isTerminal(lastStatus)) {
            return finish();
          }
        }
      } catch {
        // network blip — keep polling
      }
      timer = setTimeout(tickPoll, 2000);
    };

    void tickPoll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  },

  async getJobPace(runId: string): Promise<JobPace | null> {
    // Pace endpoint is best-effort — a 404 (job purged) or 401 (no auth)
    // shouldn't surface as a hard error in the Monitor. Treat all
    // non-2xx as null and the UI degrades to the existing status text.
    return call<JobPace>(`/jobs/${runId}/pace`);
  },
  async getOrgInventory(): Promise<OrgInventory | null> {
    // The inventory endpoint pulls the connected-org from the cookie/dep,
    // not the URL path — same shape as the existing /org/inventory but
    // with cache + pack detection. Returns null in unauthenticated dev.
    return call<OrgInventory>("/org/inventory-detail");
  },
  async cancelRun(id: string): Promise<void> {
    await apiCall<void>(`/api/jobs/${id}/cancel`, { method: "POST" });
  },
  async retryStep(id: string, step: string): Promise<void> {
    await apiCall<void>(`/api/jobs/${id}/retry-step`, { method: "POST", body: { step_name: step } });
  },
  // PR 6 — Tab Extensions chat surface.
  async tabExtensionsChat(payload: {
    pack: Record<string, unknown>;
    user_message: string;
    history?: { role: "user" | "assistant"; content: string }[];
    base_url?: string;
    current_proposal?: Record<string, unknown> | null;
  }): Promise<{
    proposed_bundle: Record<string, unknown> | null;
    preview_html: string | null;
    rationale: string | null;
    iterations: number;
    errors: string[];
  } | null> {
    return apiSoft("/api/tab-extensions/chat", {
      method: "POST",
      body: payload,
    });
  },
  async tabExtensionsPreview(payload: {
    pack: Record<string, unknown>;
    bundle: Record<string, unknown>;
    base_url?: string;
  }): Promise<{ preview_html: string | null; errors: string[] } | null> {
    return apiSoft("/api/tab-extensions/preview", {
      method: "POST",
      body: payload,
    });
  },

  // PR 5 — Org Polisher (Phase 9 + on-demand /polish endpoint).
  async polishOrg(useCache = true): Promise<{
    severity: "green" | "amber" | "red";
    message: string;
    checks: {
      name: string;
      severity: "green" | "amber" | "red";
      title: string;
      detail: string;
      runbook?: string | null;
      fix_action?: string | null;
      metrics?: Record<string, unknown>;
    }[];
    ran_at: string;
    cached: boolean;
    cache_ttl_seconds: number;
  } | null> {
    return apiSoft("/api/org/polish", {
      method: "POST",
      body: { use_cache: useCache },
    });
  },
  async preflight(profileId: string, salesCloud: boolean, serviceCloud: boolean): Promise<{
    ok: boolean;
    checks?: { name: string; passed: boolean; message?: string }[];
    warnings?: string[];
  } | null> {
    return apiSoft("/api/jobs/preflight", {
      method: "POST",
      body: {
        industry_pack_id: profileId,
        include_sales_cloud: salesCloud,
        include_service_cloud: serviceCloud,
      },
    });
  },

  // ── Tableau Cloud PAT profiles (PR 1 Week 3) ──
  async listTableauProfiles(): Promise<TableauProfile[]> {
    const raw = await call<RawTableauProfile[]>("/tableau-cloud/profiles");
    if (!raw) return [];
    return raw.map(mapTableauProfile);
  },
  async createTableauProfile(payload: {
    label: string;
    serverUrl: string;
    siteName: string;
    patName: string;
    patSecret: string;
    isDefault: boolean;
  }): Promise<TableauProfile> {
    const raw = await apiCall<RawTableauProfile>(
      "/api/tableau-cloud/profiles",
      {
        method: "POST",
        body: {
          label: payload.label,
          server_url: payload.serverUrl,
          site_name: payload.siteName,
          pat_name: payload.patName,
          pat_secret: payload.patSecret,
          is_default: payload.isDefault,
        },
      },
    );
    return mapTableauProfile(raw);
  },
  async deleteTableauProfile(id: string): Promise<void> {
    await apiCall<void>(`/api/tableau-cloud/profiles/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  async testTableauPat(payload: TableauPatOneOff): Promise<{
    ok: boolean;
    detail: string | null;
    user_name: string | null;
  }> {
    const raw = await apiCall<{ ok: boolean; detail: string | null; user_name: string | null }>(
      "/api/tableau-cloud/test-pat",
      {
        method: "POST",
        body: {
          server_url: payload.serverUrl,
          site_name: payload.siteName,
          pat_name: payload.patName,
          pat_secret: payload.patSecret,
        },
      },
    );
    return raw;
  },
};

export type RealApi = typeof realApi;
